import { useEffect, useState, useCallback, useRef } from "react";
import { getActiveRunInstance } from "../services/runInstance";

interface IuseFetchRunInstance {
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  fetchOnMount?: boolean; // Controls whether to fetch data immediately on mount
}

const useFetchRunInstance = ({
  setIsLoading,
  fetchOnMount = true, // Default to true for backward compatibility
}: IuseFetchRunInstance) => {
  const [runInstance, setRunInstance] = useState<any>([]);
  const [isFetching, setIsFetching] = useState<boolean>(false); // Internal loading state for Autocomplete

  // Use refs for guards and mutable props so the callback identity stays stable,
  // preventing useEffect re-triggers from unrelated re-renders.
  const hasFetchedRef = useRef<boolean>(false);
  const isFetchingRef = useRef<boolean>(false);
  const setIsLoadingRef = useRef(setIsLoading);
  setIsLoadingRef.current = setIsLoading;

  const fetchRunInstance = useCallback(async () => {
    // Prevent duplicate fetches AND concurrent in-flight requests
    if (hasFetchedRef.current || isFetchingRef.current) return;

    isFetchingRef.current = true;
    setIsFetching(true);
    setIsLoadingRef.current(true);
    try {
      const result = await getActiveRunInstance();
      setRunInstance(result);
      hasFetchedRef.current = true;
    } catch (err) {
      console.error("Error fetching run instances:", err);
      setRunInstance([]);
    } finally {
      isFetchingRef.current = false;
      setIsFetching(false);
      setIsLoadingRef.current(false);
    }
  }, []); // No deps — uses refs for all mutable values

  useEffect(() => {
    if (fetchOnMount) {
      fetchRunInstance();
    }
  }, [fetchOnMount, fetchRunInstance]);

  return [runInstance, fetchRunInstance, isFetching] as const;
};

export default useFetchRunInstance;
